import { useState } from "react";
import { Camera, Trash2, Undo2, Plus, Clock } from "lucide-react";
import { clsx } from "clsx";
import {
  useSnapshots,
  useCreateSnapshot,
  useDeleteSnapshot,
  useRollbackSnapshot,
} from "@/hooks/useSnapshots";

interface SnapshotPanelProps {
  vmId: number;
}

/**
 * Panel for managing Proxmox snapshots of a virtual machine.
 *
 * Renders a table of existing snapshots (name, description, date, RAM state)
 * with per-row actions for rollback and deletion.  A collapsible form at the
 * top allows creating new snapshots with an optional description and RAM
 * state inclusion.
 *
 * Destructive actions (rollback and delete) require an explicit confirmation
 * step via a yellow/red inline dialog before they are executed.
 *
 * Uses the `useSnapshots`, `useCreateSnapshot`, `useDeleteSnapshot`, and
 * `useRollbackSnapshot` hooks which manage server state via TanStack Query.
 *
 * @param vmId - The internal database ID of the VM whose snapshots to manage.
 */
export function SnapshotPanel({ vmId }: SnapshotPanelProps) {
  const { data: snapshots, isLoading } = useSnapshots(vmId);
  const createMutation = useCreateSnapshot(vmId);
  const deleteMutation = useDeleteSnapshot(vmId);
  const rollbackMutation = useRollbackSnapshot(vmId);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [includeRam, setIncludeRam] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: "delete" | "rollback";
    snapName: string;
  } | null>(null);

  const handleCreate = () => {
    if (!name.trim()) return;
    createMutation.mutate(
      { name: name.trim(), description, include_ram: includeRam },
      {
        onSuccess: () => {
          setName("");
          setDescription("");
          setIncludeRam(false);
          setShowForm(false);
        },
      }
    );
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    if (confirmAction.type === "delete") {
      deleteMutation.mutate(confirmAction.snapName, {
        onSuccess: () => setConfirmAction(null),
      });
    } else {
      rollbackMutation.mutate(confirmAction.snapName, {
        onSuccess: () => setConfirmAction(null),
      });
    }
  };

  const isBusy =
    createMutation.isPending ||
    deleteMutation.isPending ||
    rollbackMutation.isPending;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-200">Snapshots</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={isBusy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          New Snapshot
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm text-gray-400">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="pre-upgrade"
                className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                maxLength={40}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-400">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Before system upgrade"
                className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={includeRam}
                onChange={(e) => setIncludeRam(e.target.checked)}
                className="rounded border-gray-600"
              />
              Include RAM state (slower, more disk usage)
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={!name.trim() || isBusy}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 transition-colors hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmAction && (
        <div className="mb-4 rounded-lg border border-yellow-600/30 bg-yellow-900/20 p-4">
          <p className="text-sm text-yellow-200">
            {confirmAction.type === "rollback"
              ? `Rollback VM to snapshot "${confirmAction.snapName}"? The VM will restart with the state from this snapshot.`
              : `Delete snapshot "${confirmAction.snapName}"? This cannot be undone.`}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleConfirm}
              disabled={isBusy}
              className={clsx(
                "rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50",
                confirmAction.type === "rollback"
                  ? "bg-yellow-600 hover:bg-yellow-700"
                  : "bg-red-600 hover:bg-red-700"
              )}
            >
              {isBusy
                ? "Processing..."
                : confirmAction.type === "rollback"
                  ? "Confirm Rollback"
                  : "Confirm Delete"}
            </button>
            <button
              onClick={() => setConfirmAction(null)}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 transition-colors hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Snapshot list */}
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading snapshots...</p>
      ) : !snapshots || snapshots.length === 0 ? (
        <p className="text-sm text-gray-400">
          No snapshots yet. Create one to save the current VM state.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Description
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  RAM
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {snapshots.map((snap) => (
                <tr key={snap.name}>
                  <td className="px-3 py-2 font-medium text-gray-200">
                    {snap.name}
                  </td>
                  <td className="px-3 py-2 text-gray-400">
                    {snap.description || "-"}
                  </td>
                  <td className="px-3 py-2 text-gray-300">
                    {snap.snaptime ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 text-gray-500" />
                        {new Date(snap.snaptime * 1000).toLocaleString()}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={clsx(
                        "rounded-full px-2 py-0.5 text-xs font-medium",
                        snap.vmstate
                          ? "bg-purple-500/20 text-purple-400"
                          : "bg-gray-500/20 text-gray-400"
                      )}
                    >
                      {snap.vmstate ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() =>
                          setConfirmAction({
                            type: "rollback",
                            snapName: snap.name,
                          })
                        }
                        disabled={isBusy}
                        title="Rollback to this snapshot"
                        className="rounded p-1.5 text-yellow-400 transition-colors hover:bg-yellow-900/30 disabled:opacity-50"
                      >
                        <Undo2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          setConfirmAction({
                            type: "delete",
                            snapName: snap.name,
                          })
                        }
                        disabled={isBusy}
                        title="Delete snapshot"
                        className="rounded p-1.5 text-red-400 transition-colors hover:bg-red-900/30 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
